/**
 * types/supabase/UserSyncSettings.ts
 * User sync settings stored server-side
 *
 * Universal preferences that apply across all of user's devices.
 * Provider preference is separate from connection status.
 *
 * Architecture: Task 2.6 - Server-Side Sync Settings
 */

import type { SyncProviderType } from '@/types/sync/SyncProvider';
import type { ConflictResolutionStrategy } from '@/types/sync/SyncSettings';

/**
 * User sync settings stored server-side
 *
 * Universal preferences that apply across all of user's devices.
 * Provider preference (activeSyncProvider) is separate from connection status
 * (which is tracked in oauth_cloud_tokens.disconnected_at).
 */
export interface UserSyncSettings {
  /** Unique record ID */
  id: string;

  /** User ID (foreign key to auth.users) */
  userId: string;

  /** User's preferred sync provider (NULL = not configured) */
  activeSyncProvider: SyncProviderType | null;

  /** Whether automatic background sync is enabled */
  syncEnabled: boolean;

  /** Sync interval in minutes (1, 5, 10, 15, 30) */
  syncInterval: number;

  /** Whether to sync when closing app/tab */
  syncOnSessionClose: boolean;

  /** Whether to sync when logging in */
  syncOnLogin: boolean;

  /** Strategy for resolving sync conflicts */
  conflictResolution: ConflictResolutionStrategy;

  /** Optimistic concurrency control version */
  version: number;

  /** When record was created */
  createdAt: string;

  /** When record was last updated */
  updatedAt: string;
}

/**
 * Insert type for creating new user sync settings
 */
export type UserSyncSettingsInsert = Omit<UserSyncSettings, 'id' | 'version' | 'createdAt' | 'updatedAt'>;

/**
 * Update type for modifying existing user sync settings
 */
export type UserSyncSettingsUpdate = Partial<Omit<UserSyncSettings, 'id' | 'userId' | 'version' | 'createdAt' | 'updatedAt'>>;

/**
 * Default sync settings values
 * Used when creating settings for the first time
 */
export const DEFAULT_USER_SYNC_SETTINGS: Omit<UserSyncSettingsInsert, 'userId'> = {
  activeSyncProvider: null,
  syncEnabled: false,
  syncInterval: 1, // 1 minute default (frequent syncs reduce data loss window)
  syncOnSessionClose: true,
  syncOnLogin: false,
  conflictResolution: 'last-write-wins'
};
